import { HeaderCell } from '@/components/ats/Table/tableCells/HeaderCell';
import { KText } from '@/ui-library/components';
import {
  CancelledBy,
  DepartmentName,
  DestinationStage,
  InterviewDateTime,
  InterviewDuration,
  InterviewLocation,
  InterviewName,
  InterviewStatus,
  InterviewType,
  Interviewers,
  JobName,
  NameCell,
  OfferAcceptanceStatus,
  OfferRevision,
  OfficeNames,
  RejectionReason,
  RejectionType,
  RescheduledBy,
  ScheduledBy,
  ScreenedBy,
  Source,
  SourcedByName,
  StageName,
  StartingStage,
  TimeToHire,
  TimeToOffer,
} from '../cells/PerformanceDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const usePerformanceDrilldownConfig = ({
  drilldownHeading = 'Performance',
  onSort: _onSort = () => {},
  currentSort: _currentSort = {},
}) => {
  const getColumnConfig = () => {
    const columnsMap = {
      candidateName: {
        title: 'Candidate names',
        columnConfig: false,
        Header: renderDrilldownHeader('Candidate names'),
        minWidth: 200,
        accessor: 'candidate_name',
        sort: true,
        Cell: ({ row }) => NameCell(row),
      },
      sourcedByName: {
        title: 'Sourced by',
        Header: renderDrilldownHeader('Sourced by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => SourcedByName(row),
      },
      jobName: {
        title: 'Job names',
        Header: renderDrilldownHeader('Job names'),
        minWidth: 200,
        accessor: 'job_name',
        sort: true,
        Cell: ({ row }) => JobName(row),
      },
      startingStage: {
        title: 'Starting stage',
        Header: renderDrilldownHeader('Starting stage'),
        minWidth: 200,
        accessor: 'starting_stage',
        sort: true,
        Cell: ({ row }) => StartingStage(row),
      },
      destinationStage: {
        title: 'Destination stage',
        Header: renderDrilldownHeader('Destination stage'),
        minWidth: 200,
        accessor: 'destination_stage',
        sort: true,
        Cell: ({ row }) => DestinationStage(row),
      },
      screenedBy: {
        title: 'Screened by',
        Header: renderDrilldownHeader('Screened by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScreenedBy(row),
      },
      interviewName: {
        title: 'Interview name',
        Header: renderDrilldownHeader('Interview name'),
        minWidth: 200,
        accessor: 'interview_name',
        sort: true,
        Cell: ({ row }) => InterviewName(row),
      },
      interviewStatus: {
        title: 'Interview status',
        Header: renderDrilldownHeader('Interview status'),
        minWidth: 200,
        accessor: 'interview_status',
        sort: true,
        Cell: ({ row }) => InterviewStatus(row),
      },
      interviewers: {
        title: 'Interviewers',
        Header: () => (
          <HeaderCell
            title="Interviewers"
            variant="text"
            textTransform="none"
          />
        ),
        minWidth: 200,
        accessor: 'interviewers',
        Cell: ({ row }) => Interviewers(row),
      },
      interviewDateTime: {
        title: 'Interview date & time',
        Header: renderDrilldownHeader('Interview date & time'),
        minWidth: 200,
        accessor: 'interview_date',
        sort: true,
        Cell: ({ row }) => InterviewDateTime(row),
      },
      stageName: {
        title: 'Stage name',
        Header: renderDrilldownHeader('Stage name'),
        minWidth: 200,
        accessor: 'interview_stage_name',
        sort: true,
        Cell: ({ row }) => StageName(row),
      },
      scheduledBy: {
        title: 'Scheduled by',
        Header: renderDrilldownHeader('Scheduled by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScheduledBy(row),
      },
      rescheduledBy: {
        title: 'Rescheduled by',
        Header: renderDrilldownHeader('Rescheduled by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => RescheduledBy(row),
      },
      conductedBy: {
        title: 'Conducted by',
        Header: renderDrilldownHeader('Conducted by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScheduledBy(row),
      },
      offerAcceptanceStatus: {
        title: 'Offer acceptance status',
        Header: renderDrilldownHeader('Offer acceptance status'),
        minWidth: 200,
        accessor: 'offer_status',
        sort: true,
        Cell: ({ row }) => OfferAcceptanceStatus(row),
      },
      timeToOffer: {
        title: 'Time to offer',
        Header: renderDrilldownHeader('Time to offer'),
        minWidth: 200,
        accessor: 'time_to_offer',
        sort: true,
        Cell: ({ row }) => TimeToOffer(row),
      },
      offerRevision: {
        title: 'Offer revision',
        Header: renderDrilldownHeader('Offer revision'),
        minWidth: 200,
        accessor: 'offer_revisions_count',
        sort: true,
        Cell: ({ row }) => OfferRevision(row),
      },
      timeToHire: {
        title: 'Time to hire',
        Header: renderDrilldownHeader('Time to hire'),
        minWidth: 200,
        accessor: 'time_to_hire',
        sort: true,
        Cell: ({ row }) => TimeToHire(row),
      },
      source: {
        title: 'Source',
        Header: renderDrilldownHeader('Source'),
        minWidth: 200,
        accessor: 'appl_contact_source',
        sort: true,
        Cell: ({ row }) => Source(row),
      },
      rejectionType: {
        title: 'Rejection type',
        Header: renderDrilldownHeader('Rejection type'),
        minWidth: 200,
        accessor: 'rejection_type',
        sort: true,
        Cell: ({ row }) => RejectionType(row),
      },
      rejectionReason: {
        title: 'Rejection reason',
        Header: renderDrilldownHeader('Rejection reason'),
        minWidth: 200,
        accessor: 'rejection_reason',
        sort: true,
        Cell: ({ row }) => RejectionReason(row),
      },
      offerBy: {
        title: 'Offer created by',
        Header: renderDrilldownHeader('Offer created by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScreenedBy(row),
      },
      hiredBy: {
        title: 'Hired by',
        Header: renderDrilldownHeader('Hired by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScreenedBy(row),
      },
      rejectedBy: {
        title: 'Rejected by',
        Header: renderDrilldownHeader('Rejected by'),
        minWidth: 200,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => ScreenedBy(row),
      },
      interviewType: {
        title: 'Interview type',
        Header: renderDrilldownHeader('Interview type'),
        minWidth: 160,
        accessor: 'interview_type',
        sort: true,
        Cell: ({ row }) => InterviewType(row),
      },
      interviewDuration: {
        title: 'Interview duration',
        Header: renderDrilldownHeader('Interview duration'),
        minWidth: 200,
        accessor: 'interview_duration',
        sort: true,
        Cell: ({ row }) => InterviewDuration(row),
      },
      interviewLocation: {
        title: 'Interview location',
        Header: renderDrilldownHeader('Interview location'),
        minWidth: 160,
        accessor: 'interview_location_type',
        sort: true,
        Cell: ({ row }) => InterviewLocation(row),
      },
      departmentName: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        minWidth: 160,
        accessor: 'department_name',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      officeNames: {
        title: 'Offices',
        Header: renderDrilldownHeader('Offices'),
        minWidth: 200,
        accessor: 'office_names',
        sort: true,
        Cell: ({ row }) => OfficeNames(row),
      },
      cancelledBy: {
        title: 'Canceled by',
        Header: renderDrilldownHeader('Canceled by'),
        minWidth: 180,
        accessor: 'action_user_name',
        sort: true,
        Cell: ({ row }) => CancelledBy(row),
      },
    };

    switch (drilldownHeading) {
      case 'Screened applications':
        return [
          columnsMap.candidateName,
          columnsMap.startingStage,
          columnsMap.destinationStage,
          columnsMap.jobName,
          columnsMap.screenedBy,
        ];
      case 'Interviews scheduled':
        return [
          columnsMap.candidateName,
          columnsMap.interviewName,
          columnsMap.interviewStatus,
          columnsMap.interviewers,
          columnsMap.interviewDateTime,
          columnsMap.interviewType,
          columnsMap.interviewDuration,
          columnsMap.interviewLocation,
          columnsMap.jobName,
          columnsMap.stageName,
          columnsMap.departmentName,
          columnsMap.officeNames,
          columnsMap.scheduledBy,
        ];
      case 'Interview reschedules':
        return [
          columnsMap.candidateName,
          columnsMap.interviewName,
          columnsMap.interviewStatus,
          columnsMap.interviewers,
          columnsMap.interviewDateTime,
          columnsMap.interviewType,
          columnsMap.interviewDuration,
          columnsMap.interviewLocation,
          columnsMap.jobName,
          columnsMap.stageName,
          columnsMap.departmentName,
          columnsMap.officeNames,
          columnsMap.rescheduledBy,
        ];
      case 'Interviews conducted':
        return [
          columnsMap.candidateName,
          columnsMap.interviewName,
          columnsMap.interviewDateTime,
          columnsMap.interviewType,
          columnsMap.interviewDuration,
          columnsMap.interviewLocation,
          columnsMap.jobName,
          columnsMap.stageName,
          columnsMap.departmentName,
          columnsMap.officeNames,
          columnsMap.conductedBy,
        ];
      case 'Interviews canceled':
        return [
          columnsMap.candidateName,
          columnsMap.interviewName,
          columnsMap.interviewers,
          columnsMap.interviewType,
          columnsMap.interviewDateTime,
          columnsMap.interviewDuration,
          columnsMap.interviewLocation,
          columnsMap.jobName,
          columnsMap.stageName,
          columnsMap.departmentName,
          columnsMap.officeNames,
          columnsMap.cancelledBy,
        ];
      case 'Offers sent':
        return [
          columnsMap.candidateName,
          columnsMap.timeToOffer,
          columnsMap.offerAcceptanceStatus,
          columnsMap.jobName,
          columnsMap.offerBy,
        ];
      case 'Offer revisions done':
        return [
          columnsMap.candidateName,
          columnsMap.offerRevision,
          columnsMap.timeToOffer,
          columnsMap.offerAcceptanceStatus,
          columnsMap.jobName,
          columnsMap.offerBy,
        ];
      case 'Avg. time to offer':
        return [
          columnsMap.candidateName,
          columnsMap.timeToOffer,
          columnsMap.offerAcceptanceStatus,
          columnsMap.jobName,
          columnsMap.offerBy,
        ];
      case 'Offer acceptance rate':
        return [
          columnsMap.candidateName,
          columnsMap.timeToOffer,
          columnsMap.jobName,
          columnsMap.offerBy,
        ];

      case 'Hired':
        return [
          columnsMap.candidateName,
          columnsMap.timeToHire,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.hiredBy,
        ];
      case 'Rejected':
        return [
          columnsMap.candidateName,
          columnsMap.rejectionType,
          columnsMap.rejectionReason,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.rejectedBy,
        ];
      case 'Rejections':
        return [
          columnsMap.candidateName,
          columnsMap.rejectionType,
          columnsMap.rejectionReason,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.rejectedBy,
        ];
      case 'Avg. time to hire':
        return [
          columnsMap.candidateName,
          columnsMap.timeToHire,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.hiredBy,
        ];
      case 'Time to hire':
        return [
          columnsMap.candidateName,
          columnsMap.timeToHire,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.hiredBy,
        ];
      default:
        return [
          columnsMap.candidateName,
          columnsMap.source,
          columnsMap.jobName,
          columnsMap.sourcedByName,
        ];
    }
  };

  return { columnConfig: getColumnConfig() };
};
